import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { NaatiVisaLanding } from "@/content/naati-corpus/visa-subclasses";
import { NAATI_DOC_META, NAATI_VISA_META } from "@/content/naati-corpus/meta.gen";

function visaFaqs(v: NaatiVisaLanding) {
  return [
    { q: `${v.nameTh} ต้องแปลเอกสารอะไรเป็นภาษาอังกฤษ?`, a: v.docsTh.map((d) => d.doc).join(" · ") },
    { q: `ใครยื่น ${v.nameTh} ได้?`, a: v.whoTh },
    { q: `ทำไมเอกสารของ ${v.nameTh} ถูกขอเพิ่มบ่อย?`, a: v.pitfallsTh.join(" · ") },
    { q: `แปลเอกสารสำหรับ ${v.nameTh} ใช้เวลานานเท่าไร?`, a: `${v.turnaroundTh} — ราคาคิดตามจำนวนหน้าหรือจำนวนคำ ขอใบเสนอราคาฟรีก่อนเริ่มงานได้ทุกครั้ง` },
    {
      q: "ใช้คำแปลจากนักแปลทั่วไปแทน NAATI ได้ไหม?",
      a: "โดยทั่วไป Department of Home Affairs กำหนดให้คำแปลจากภายในออสเตรเลียมาจากนักแปล NAATI ส่วนคำแปลจากต่างประเทศต้องระบุชื่อ ที่อยู่ คุณวุฒิ และประสบการณ์ของผู้แปล การใช้คำแปล NAATI Certified จึงเป็นทางที่ปลอดภัยที่สุด และเป็นสิ่งที่เจ้าหน้าที่คุ้นเคย",
    },
    {
      q: "คำแปล NAATI ต้องผ่านกรมการกงสุลหรือสถานทูตอีกไหม?",
      a: "สำหรับการยื่นวีซ่าออสเตรเลียโดยทั่วไปไม่ต้อง เพราะ NAATI เป็นระบบรับรองคำแปลของออสเตรเลียเอง แต่ถ้าเอกสารชุดเดียวกันจะใช้กับหน่วยงานไทยหรือประเทศที่สาม อาจต้องทำนิติกรณ์กรมการกงสุลเพิ่มแยกกัน — NAATI และการรับรองคำแปลของกรมการกงสุลใช้แทนกันไม่ได้",
    },
    {
      q: "ส่งไฟล์สแกนแทนต้นฉบับได้ไหม?",
      a: "ได้ สำหรับงานแปล NAATI ใช้สแกนสีความละเอียดสูงที่อ่านตราประทับและลายเซ็นได้ครบทุกหน้า ต้นฉบับจำเป็นเฉพาะกรณีต้องทำนิติกรณ์กรมการกงสุลควบคู่",
    },
    {
      q: "ผลการพิจารณาวีซ่ารับประกันได้ไหม?",
      a: "ไม่ได้ การอนุมัติวีซ่าเป็นดุลพินิจของ Department of Home Affairs เท่านั้น เราดูแลเฉพาะความถูกต้องและความครบถ้วนของคำแปล และแก้ไขให้ฟรีหากเกิดจากความผิดพลาดของผู้แปล",
    },
  ];
}

export const Route = createFileRoute("/naati/visa/$subclass")({
  loader: async ({ params }): Promise<{ v: NaatiVisaLanding }> => {
    const { getNaatiVisaLanding } = await import("@/content/naati-corpus/visa-subclasses");
    const v = getNaatiVisaLanding(params.subclass);
    if (!v) throw notFound();
    return { v };
  },
  component: NaatiVisaPageTH,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบประเภทวีซ่า" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { v } = loaderData;
    const title = `${v.titleTh} (2026)`;
    const description = `เอกสารที่ต้องแปล NAATI สำหรับ ${v.nameTh} — เช็กลิสต์ เอกสารที่ถูกขอเพิ่มบ่อย และ ${v.turnaroundTh}`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/naati/visa/${params.subclass}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/naati/visa/${params.subclass}` }],
        keywords: [
          v.titleTh,
          `แปลเอกสาร วีซ่า ${v.code}`,
          `เอกสาร subclass ${v.code}`,
          `naati subclass ${v.code}`,
          "แปลเอกสาร naati ออสเตรเลีย",
          "นักแปล naati รับรอง",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: `${site}/` },
              { name: "แปลเอกสาร NAATI", url: `${site}/naati` },
              { name: v.nameTh, url: `${site}/naati/visa/${params.subclass}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(visaFaqs(v))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/naati/visa/${params.subclass}`)) },
      ],
    };
  },
});

function NaatiVisaPageTH() {
  const { v } = Route.useLoaderData();
  const faqs = visaFaqs(v);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/naati" className="hover:underline">แปลเอกสาร NAATI</a> <span>/</span> <span>{v.nameTh}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{v.titleTh}</h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">
          {v.nameTh} ต้องยื่นเอกสารภาษาไทยพร้อมคำแปลภาษาอังกฤษที่ตรวจสอบย้อนกลับได้ ชุดหลักมี {v.docsTh.length} กลุ่มเอกสาร
          และ{v.turnaroundTh} จุดที่ทำให้เสียเวลามากที่สุดคือชื่อสะกดไม่ตรงหนังสือเดินทางและการแปลไม่ครบทุกหน้า
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">ขอใบเสนอราคาฟรี →</a>
          <a href="/naati/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">คำถามที่พบบ่อย NAATI</a>
          <a href={`/en/naati/visa/${v.slug}`} className="rounded-md border px-4 py-2 font-medium hover:bg-muted">English</a>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ใครยื่นวีซ่านี้</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{v.whoTh}</p>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">เอกสารที่ต้องแปล NAATI สำหรับ subclass {v.code}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 pe-3 font-semibold">เอกสาร</th>
                  <th className="py-2 font-semibold">ข้อควรระวังก่อนส่งแปล</th>
                </tr>
              </thead>
              <tbody>
                {v.docsTh.map((d: { doc: string; note: string }, i: number) => (
                  <tr key={i} className="border-b last:border-0 align-top">
                    <td className="py-2 pe-3 font-medium">{d.doc}</td>
                    <td className="py-2 text-muted-foreground">{d.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-sm text-muted-foreground"><strong>ระยะเวลา:</strong> {v.turnaroundTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">สาเหตุที่เอกสารถูกขอเพิ่มหรือถูกตีกลับ</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {v.pitfallsTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">คำถามที่พบบ่อย — {v.nameTh}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">แปล NAATI สำหรับวีซ่าประเภทอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_VISA_META.filter((x) => x.slug !== v.slug).map((x) => (
              <a key={x.slug} href={`/naati/visa/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                subclass {x.code}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">แปล NAATI แยกตามประเภทเอกสาร</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NAATI_DOC_META.map((x) => (
              <a key={x.slug} href={`/naati/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleTh}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          แหล่งอ้างอิงทางการ:{" "}
          <a href={v.officialUrl} rel="nofollow noopener" target="_blank" className="text-primary underline">
            {v.officialLabel}
          </a>{" "}
          — ข้อกำหนดและระยะเวลาพิจารณาอาจเปลี่ยนแปลง โปรดตรวจสอบกับหน่วยงานก่อนดำเนินการ
          การอนุมัติวีซ่าเป็นดุลพินิจของหน่วยงานตรวจคนเข้าเมืองเท่านั้น
        </p>

        <PageGallery category="naati" seed="naati-visa" locale="th" />
        <FaqSourceNote topic="translation" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}